import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const allStatusFilters = {
  yes: true,
  no: true,
  noresponse: true,
};

const statusChip = (attending) => {
  if (attending === true) return <Chip size="small" label="Yes" color="success" clickable={false} onClick={() => {}} />;
  if (attending === false) return <Chip size="small" label="No" color="error" clickable={false} onClick={() => {}} />;
  return <Chip size="small" label="No response" clickable={false} onClick={() => {}} />;
};

const SocialEventRsvpsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilters, setStatusFilters] = useState(allStatusFilters);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await unifiedApi.getSocialEventRsvps(id);
      setData(result);
    } catch {
      setError('Failed to load RSVPs.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRecipients = useMemo(() => {
    const recipients = data?.recipients || [];
    const q = search.trim().toLowerCase();
    return recipients.filter((r) => {
      if (r.attending === true && !statusFilters.yes) return false;
      if (r.attending === false && !statusFilters.no) return false;
      if (r.attending !== true && r.attending !== false && !statusFilters.noresponse) return false;

      // Search filter (name or email)
      if (q) {
        const name = (r.fullName || '').toLowerCase();
        const email = (r.email || '').toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [data, statusFilters, search]);

  const selectedStatusFilters = useMemo(
    () => Object.keys(statusFilters).filter((key) => statusFilters[key]),
    [statusFilters]
  );

  const allSelected = selectedStatusFilters.length === Object.keys(allStatusFilters).length;

  const handleStatusFilterChange = (_event, values) => {
    setStatusFilters({
      yes: values.includes('yes'),
      no: values.includes('no'),
      noresponse: values.includes('noresponse'),
    });
  };

  if (loading) return <AdminLayout><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></AdminLayout>;
  if (error) return <AdminLayout><Alert severity="error">{error}</Alert></AdminLayout>;
  if (!data) return <AdminLayout><Alert severity="warning">No RSVP data.</Alert></AdminLayout>;

  const totalRecipients = data.recipients?.length || 0;

  return (
    <AdminLayout>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/social-events')}>Back</Button>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>RSVPs</Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 3 }}
          sx={{ flexWrap: 'wrap' }}
        >
          <Typography><strong>Attending:</strong> {data.attending}</Typography>
          <Typography><strong>Not attending:</strong> {data.notAttending}</Typography>
          <Typography><strong>No response:</strong> {data.noResponse}</Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: { xs: '100%', md: 'auto' } }}
            role="group"
            aria-label="Filter by RSVP status"
          >
            <ToggleButton
              value="all"
              selected={allSelected}
              size="small"
              onClick={() => setStatusFilters(allStatusFilters)}
              aria-label="All"
            >
              All ({totalRecipients})
            </ToggleButton>
            <ToggleButtonGroup
              value={selectedStatusFilters}
              size="small"
              onChange={handleStatusFilterChange}
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="yes" aria-label="Attending">Yes ({data.attending})</ToggleButton>
              <ToggleButton value="no" aria-label="Not attending">No ({data.notAttending})</ToggleButton>
              <ToggleButton value="noresponse" aria-label="No response">No response ({data.noResponse})</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            size="small"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 260 }, flex: 1, width: { xs: '100%', md: 'auto' } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setSearch('')} startIcon={<ClearIcon fontSize="small" />} sx={{ minWidth: 0 }}>
                      Clear
                    </Button>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Stack>
      </Paper>

      {isMobile ? (
        <Stack spacing={1.5}>
          {filteredRecipients.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No recipients match the current filters.</Typography>
            </Paper>
          ) : (
            filteredRecipients.map((r) => (
              <Paper key={r.userTenantId} sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                      {r.fullName}
                    </Typography>
                    {statusChip(r.attending)}
                  </Stack>
                  {r.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {r.email}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Responded: {r.respondedAt ? new Date(r.respondedAt).toLocaleString() : '—'}
                  </Typography>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Responded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      No recipients match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecipients.map((r) => (
                    <TableRow key={r.userTenantId}>
                      <TableCell>{r.fullName}</TableCell>
                      <TableCell>{r.email || '—'}</TableCell>
                      <TableCell>{statusChip(r.attending)}</TableCell>
                      <TableCell>{r.respondedAt ? new Date(r.respondedAt).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </AdminLayout>
  );
};

export default SocialEventRsvpsPage;
